import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { MachinaTheme, defaultTheme } from './types';
import {
  themeRegistry,
  getThemeById,
  getDefaultTheme,
  isLightTheme,
  getThemePreviews,
  ThemePreview
} from './registry';

const STORAGE_KEY = 'machina-theme-id';

interface ThemeEngineContextValue {
  // Current theme
  theme: MachinaTheme;
  themeId: string;
  isLight: boolean;

  // Theme switching
  setTheme: (theme: MachinaTheme) => void;
  setThemeById: (id: string) => void;

  // Available themes
  availableThemes: MachinaTheme[];
  themePreviews: ThemePreview[];

  // Utilities
  getThemeById: (id: string) => MachinaTheme | undefined;
}

const ThemeEngineContext = createContext<ThemeEngineContextValue | null>(null);

/**
 * Convert camelCase to kebab-case
 */
function toKebabCase(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Apply theme as CSS custom properties
 */
function applyTheme(theme: MachinaTheme) {
  const root = document.documentElement;

  // Set color-scheme for browser UI
  root.style.setProperty('color-scheme', isLightTheme(theme) ? 'light' : 'dark');

  // Colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${toKebabCase(key)}`, value);
  });

  // Typography
  Object.entries(theme.typography).forEach(([key, value]) => {
    root.style.setProperty(`--${toKebabCase(key)}`, String(value));
  });

  // Spacing
  Object.entries(theme.spacing).forEach(([key, value]) => {
    root.style.setProperty(`--spacing-${key}`, value);
  });

  // Borders
  Object.entries(theme.borders).forEach(([key, value]) => {
    root.style.setProperty(`--${toKebabCase(key)}`, value);
  });

  // Shadows
  Object.entries(theme.shadows).forEach(([key, value]) => {
    root.style.setProperty(`--shadow-${key}`, value);
  });

  // Effects
  Object.entries(theme.effects).forEach(([key, value]) => {
    root.style.setProperty(`--${toKebabCase(key)}`, value);
  });
}

/**
 * Back-compat map for theme ids that were renamed. Keeps existing users on the
 * theme they picked even though the underlying id string changed.
 */
const LEGACY_THEME_ID_ALIASES: Record<string, string> = {
  // 'melnibone' was the original default theme id; renamed during IP scrub.
  melnibone: 'obsidian-court',
};

/**
 * Load saved theme ID from localStorage, applying any legacy-id migration and
 * rewriting the stored value so the alias only ever needs to run once.
 */
function loadSavedThemeId(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LEGACY_THEME_ID_ALIASES[stored]) {
      const migrated = LEGACY_THEME_ID_ALIASES[stored];
      try {
        localStorage.setItem(STORAGE_KEY, migrated);
      } catch {
        // best-effort rewrite; alias still resolves below
      }
      return migrated;
    }
    return stored;
  } catch {
    return null;
  }
}

/**
 * Save theme ID to localStorage
 */
function saveThemeId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // localStorage might be unavailable
  }
}

/**
 * Get initial theme - try to load from storage, fall back to default
 */
function getInitialTheme(explicitTheme?: MachinaTheme): MachinaTheme {
  if (explicitTheme) {
    return explicitTheme;
  }

  const savedId = loadSavedThemeId();
  if (savedId) {
    const savedTheme = getThemeById(savedId);
    if (savedTheme) {
      return savedTheme;
    }
  }

  return getDefaultTheme();
}

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: MachinaTheme;
}

export function ThemeProvider({ children, initialTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<MachinaTheme>(() => getInitialTheme(initialTheme));

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Set theme directly
  const setTheme = useCallback((newTheme: MachinaTheme) => {
    setThemeState(newTheme);
    saveThemeId(newTheme.id);
  }, []);

  // Set theme by ID
  const setThemeById = useCallback((id: string) => {
    const newTheme = getThemeById(id);
    if (newTheme) {
      setTheme(newTheme);
    } else {
      console.warn(`Theme with id "${id}" not found`);
    }
  }, [setTheme]);

  // Memoize context value
  const contextValue = useMemo<ThemeEngineContextValue>(() => ({
    theme,
    themeId: theme.id,
    isLight: isLightTheme(theme),
    setTheme,
    setThemeById,
    availableThemes: themeRegistry,
    themePreviews: getThemePreviews(),
    getThemeById,
  }), [theme, setTheme, setThemeById]);

  return (
    <ThemeEngineContext.Provider value={contextValue}>
      {children}
    </ThemeEngineContext.Provider>
  );
}

/**
 * Hook to access the theme engine
 */
export function useThemeEngine(): ThemeEngineContextValue {
  const context = useContext(ThemeEngineContext);

  if (!context) {
    throw new Error('useThemeEngine must be used within ThemeProvider');
  }

  return context;
}

/**
 * Hook to access just the current theme (convenience)
 */
export function useTheme(): MachinaTheme {
  return useThemeEngine().theme;
}

// Re-export for convenience
export { defaultTheme, themeRegistry };
export type { MachinaTheme, ThemePreview };
